import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, BRAND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  ShieldCheck, Sparkles, ArrowRight, CheckCircle2, XCircle,
  Mail, Loader2, RotateCcw, ArrowLeft, Eye, EyeOff, Phone,
} from "lucide-react";

const TRIAL_PERKS = [
  "30-day free trial — no credit card required",
  "1 branch · 5 users · 1 GB storage",
  "Patient Reg + OPD EMR + Optometry + Billing + Reports",
  "Email support · cancel anytime",
];

const codeRe = /^[A-Z][A-Z0-9]{2,11}$/;

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Public-plan deep-link: /signup?plan_id=X redirects the fresh admin to
  // /app/checkout/{plan_id} after OTP verification.
  const preselectedPlanId = searchParams.get("plan_id") || "";

  const [step, setStep] = useState(1); // 1 = details, 2 = OTP, 3 = success
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  // Form state
  const [form, setForm] = useState({
    hospital_name: "",
    hospital_code: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
    admin_phone: "",
    address: "",
    contact_phone: "",
    also_doctor: false,
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Live availability state
  const [availability, setAvailability] = useState({
    code_taken: false, code_valid: null, email_taken: false, checking: false,
  });

  // Step 2 state
  const [signup, setSignup] = useState(null); // { signup_id, expires_at, dev_otp? }
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendBusy, setResendBusy] = useState(false);

  // Step 3 state
  const [completed, setCompleted] = useState(null); // { hospital, user, ... }

  // ---------- Live availability check (debounced) ----------
  useEffect(() => {
    const code = (form.hospital_code || "").toUpperCase();
    const email = (form.admin_email || "").toLowerCase().trim();
    if (!code && !email) {
      setAvailability({ code_taken: false, code_valid: null, email_taken: false, checking: false });
      return;
    }
    setAvailability((s) => ({ ...s, checking: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/signup/availability`, { params: { code, email } });
        setAvailability({
          code_taken: !!data.code_taken,
          code_valid: data.code_valid,
          email_taken: !!data.email_taken,
          checking: false,
        });
      } catch {
        setAvailability((s) => ({ ...s, checking: false }));
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.hospital_code, form.admin_email]);

  // ---------- Resend cooldown timer ----------
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // ---------- Validation ----------
  const codeError = useMemo(() => {
    const c = (form.hospital_code || "").toUpperCase();
    if (!c) return "";
    if (!codeRe.test(c)) return "3-12 chars, A-Z & 0-9, must start with a letter.";
    if (availability.code_taken) return "This code is already taken.";
    return "";
  }, [form.hospital_code, availability.code_taken]);

  const emailError = useMemo(() => {
    if (!form.admin_email) return "";
    if (availability.email_taken) return "An account with this email already exists.";
    return "";
  }, [form.admin_email, availability.email_taken]);

  const pwdError = useMemo(() => {
    if (!form.admin_password) return "";
    if (form.admin_password.length < 8) return "Minimum 8 characters.";
    return "";
  }, [form.admin_password]);

  const canSubmit = useMemo(() => {
    return (
      form.hospital_name.trim().length >= 2 &&
      codeRe.test((form.hospital_code || "").toUpperCase()) &&
      !availability.code_taken &&
      form.admin_name.trim().length >= 2 &&
      /\S+@\S+\.\S+/.test(form.admin_email) &&
      !availability.email_taken &&
      form.admin_password.length >= 8 &&
      !submitting
    );
  }, [form, availability, submitting]);

  // ---------- Step 1 → start ----------
  const startSignup = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/signup/start", {
        ...form,
        hospital_code: form.hospital_code.toUpperCase().trim(),
        admin_email: form.admin_email.toLowerCase().trim(),
      });
      setSignup(data);
      setOtp("");
      setStep(2);
      setResendCooldown(30);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not start signup. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Step 2 → verify ----------
  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!otp.trim() || otp.trim().length < 4) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/signup/verify", {
        signup_id: signup.signup_id,
        otp: otp.trim(),
      });
      // Persist token so user is logged in.
      localStorage.setItem("careo_token", data.access_token);
      localStorage.setItem("careo_last_hospital_code", data.hospital.code);
      setCompleted(data);
      setStep(3);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    if (resendCooldown > 0 || resendBusy) return;
    setError("");
    setResendBusy(true);
    try {
      const { data } = await api.post("/signup/resend", { signup_id: signup.signup_id });
      setSignup((s) => ({ ...s, ...data }));
      setResendCooldown(30);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not resend code.");
    } finally {
      setResendBusy(false);
    }
  };

  const goToApp = () => {
    // Force a full reload so AuthProvider picks up the freshly stored token.
    if (preselectedPlanId) {
      window.location.href = `/app/checkout/${preselectedPlanId}?action=subscribe`;
    } else {
      window.location.href = "/app";
    }
  };

  // ---------- Render ----------
  return (
    <div className="grid min-h-screen md:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden bg-brand text-white md:flex md:flex-col md:p-10">
        <div className="iris-motif absolute inset-0 opacity-50" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.16),transparent_55%),radial-gradient(circle_at_85%_85%,rgba(56,189,248,0.18),transparent_55%)]" />

        <div className="relative z-10 flex items-center gap-3">
          <img src={BRAND.logo} alt="" className="brand-logo-white h-16 w-auto" />
        </div>

        <div className="relative z-10 mt-12 max-w-lg">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-leaf-100 backdrop-blur-sm">
            <Sparkles size={12} /> Free trial · No card
          </div>
          <h1 className="mt-5 font-heading text-4xl font-semibold leading-tight">
            Start your hospital on CareO in <span className="text-leaf-100">under 2 minutes</span>.
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-white/80">
            Self-service onboarding. Verify your email, pick a hospital code, and your trial workspace is ready instantly.
          </p>

          <Card className="mt-8 border-white/15 bg-white/[0.06] p-5 text-white shadow-2xl backdrop-blur-sm">
            <div className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-white/85">Free Trial includes</div>
            <ul className="space-y-2.5 text-sm">
              {TRIAL_PERKS.map((p, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="mt-0.5 flex-none text-leaf-100" />
                  <span className="leading-snug text-white/90">{p}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div className="relative z-10 mt-auto pt-8">
          <div className="flex items-center justify-between border-t border-white/10 pt-3 text-[10px] text-white/55">
            <span className="inline-flex items-center gap-1"><ShieldCheck size={11} className="text-leaf-100" /> NABH · DPDP-ready</span>
            <span className="rounded-full bg-leaf-100/15 px-2 py-0.5 font-medium uppercase tracking-wider text-leaf-100">Secured signup</span>
          </div>
          <div className="mt-1 text-[9.5px] text-white/45">{BRAND.footer}</div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col items-center justify-center bg-white px-6 py-10 md:px-14">
        <div className="w-full max-w-md">
          {/* Mobile brand strip */}
          <div className="mb-8 flex items-center gap-3 md:hidden">
            <img src={BRAND.icon} alt="" className="h-9 w-9" />
            <div className="leading-tight">
              <div className="font-heading text-lg font-semibold text-brand">{BRAND.name}</div>
              <div className="text-[10px] text-ink-muted">{BRAND.tagline}</div>
            </div>
          </div>

          {/* Step pill */}
          <div className="mb-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-ink-muted">
            <span className={`h-1 w-8 rounded-full ${step >= 1 ? "bg-brand" : "bg-border"}`} />
            <span className={`h-1 w-8 rounded-full ${step >= 2 ? "bg-brand" : "bg-border"}`} />
            <span className={`h-1 w-8 rounded-full ${step >= 3 ? "bg-leaf" : "bg-border"}`} />
            <span className="ml-2">Step {step} of 3</span>
          </div>

          {/* ---------- Step 1: details ---------- */}
          {step === 1 && (
            <>
              <h2 className="font-heading text-3xl font-semibold">Start your free trial</h2>
              <p className="mt-1 text-sm text-ink-muted">
                Already have an account?{" "}
                <Link to="/login" className="font-medium text-brand hover:underline" data-testid="signup-go-login">Sign in</Link>
              </p>

              <form onSubmit={startSignup} className="mt-7 space-y-4" data-testid="signup-form-step1">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Hospital / Clinic name</Label>
                  <Input
                    data-testid="signup-hospital-name"
                    value={form.hospital_name}
                    onChange={(e) => set("hospital_name", e.target.value)}
                    placeholder="e.g. Sankara Eye Hospital"
                    required
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Hospital code</Label>
                  <Input
                    data-testid="signup-hospital-code"
                    value={form.hospital_code}
                    onChange={(e) => set("hospital_code", e.target.value.toUpperCase().slice(0, 12))}
                    placeholder="e.g. SANKARA"
                    required
                    className="mt-1.5 uppercase tracking-widest"
                  />
                  <div className="mt-1 flex items-center gap-1.5 text-[10px]">
                    {codeError ? (
                      <span className="inline-flex items-center gap-1 text-rose-600"><XCircle size={11} />{codeError}</span>
                    ) : form.hospital_code && availability.code_valid && !availability.code_taken && !availability.checking ? (
                      <span className="inline-flex items-center gap-1 text-leaf-700"><CheckCircle2 size={11} /> Available — your sign-in code.</span>
                    ) : (
                      <span className="text-ink-muted">3-12 chars, A-Z & 0-9. Used at sign-in to identify your hospital.</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Admin name</Label>
                    <Input
                      data-testid="signup-admin-name"
                      value={form.admin_name}
                      onChange={(e) => set("admin_name", e.target.value)}
                      placeholder="Dr. Full Name"
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Phone (optional)</Label>
                    <Input
                      data-testid="signup-admin-phone"
                      value={form.admin_phone}
                      onChange={(e) => set("admin_phone", e.target.value)}
                      placeholder="+91-XXXXXXXXXX"
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Admin email</Label>
                  <Input
                    data-testid="signup-admin-email"
                    type="email"
                    value={form.admin_email}
                    onChange={(e) => set("admin_email", e.target.value)}
                    placeholder="you@hospital.in"
                    required
                    className="mt-1.5"
                  />
                  <div className="mt-1 flex items-center gap-1.5 text-[10px]">
                    {emailError ? (
                      <span className="inline-flex items-center gap-1 text-rose-600"><XCircle size={11} />{emailError}</span>
                    ) : (
                      <span className="text-ink-muted">We'll send a 6-digit verification code to this address.</span>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Password</Label>
                  <div className="relative mt-1.5">
                    <Input
                      data-testid="signup-admin-password"
                      type={showPwd ? "text" : "password"}
                      value={form.admin_password}
                      onChange={(e) => set("admin_password", e.target.value)}
                      placeholder="Min 8 characters"
                      required
                    />
                    <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink" data-testid="signup-toggle-password">
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {pwdError && <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-rose-600"><XCircle size={11} />{pwdError}</div>}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Hospital phone</Label>
                    <Input
                      data-testid="signup-contact-phone"
                      value={form.contact_phone}
                      onChange={(e) => set("contact_phone", e.target.value)}
                      placeholder="Optional"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Address</Label>
                    <Input
                      data-testid="signup-address"
                      value={form.address}
                      onChange={(e) => set("address", e.target.value)}
                      placeholder="Optional"
                      className="mt-1.5"
                    />
                  </div>
                </div>

                {/* Optional: also register as a doctor/consultant.
                    Saves a step for solo-practitioner hospitals so they
                    show up in the OPD doctor dropdown right away. */}
                <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-secondary/40 p-3 text-xs">
                  <input
                    type="checkbox"
                    checked={form.also_doctor}
                    onChange={(e) => set("also_doctor", e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-brand"
                    data-testid="signup-also-doctor"
                  />
                  <span>
                    <b>Also register me as a doctor / consultant</b>
                    <div className="mt-0.5 text-[11px] text-ink-muted">
                      Tick this if you (the admin) also see patients. You&apos;ll appear in the OPD doctor list without a separate account. Specialisation and OPD rate can be added later from Masters → Doctors.
                    </div>
                  </span>
                </label>

                {error && (
                  <div data-testid="signup-error" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{error}</div>
                )}

                <Button
                  data-testid="signup-submit-step1"
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full bg-brand hover:bg-brand-700"
                >
                  {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Sending code…</> : <>Create my workspace <ArrowRight size={14} className="ml-2" /></>}
                </Button>

                <p className="text-center text-[10px] text-ink-muted">
                  By continuing you agree to our{" "}
                  <Link to="/legal/terms" className="underline hover:text-brand">Terms</Link>,{" "}
                  <Link to="/legal/privacy" className="underline hover:text-brand">Privacy Policy</Link> &{" "}
                  <Link to="/legal/dpa" className="underline hover:text-brand">DPA</Link>.
                </p>

                {/* Direct support / renewal helpline — shown on the trial-signup
                    form so prospects who need help understanding the product,
                    filing a complaint, or renewing an existing plan can reach
                    us in one tap. */}
                <div className="text-center">
                  <a
                    href="tel:+919616298162"
                    className="inline-flex items-center gap-1.5 rounded-full border border-brand/30 bg-brand-50 px-3 py-1 text-[11px] font-semibold text-brand hover:bg-brand-100"
                    data-testid="signup-support-phone"
                  >
                    <Phone size={12} />
                    <span className="font-normal text-ink-muted">Any query / complaint / renewal:</span>
                    <span className="font-mono">96162-98162</span>
                  </a>
                </div>
              </form>
            </>
          )}

          {/* ---------- Step 2: verify OTP ---------- */}
          {step === 2 && signup && (
            <>
              <button
                type="button"
                onClick={() => { setStep(1); setError(""); }}
                className="mb-3 inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand"
                data-testid="signup-back-to-step1"
              >
                <ArrowLeft size={12} /> Back
              </button>
              <h2 className="font-heading text-3xl font-semibold">Check your inbox</h2>
              <p className="mt-1 text-sm text-ink-muted">
                We sent a 6-digit code to <b className="text-ink">{signup.email || form.admin_email}</b>.
                Enter it below to finish creating your hospital.
              </p>

              {signup.dev_otp && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                  <b>SMTP not configured (dev mode):</b> code is <code className="rounded bg-white px-1.5 py-0.5 font-mono text-amber-900">{signup.dev_otp}</code>
                </div>
              )}

              <form onSubmit={verifyOtp} className="mt-6 space-y-4" data-testid="signup-form-step2">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Verification code</Label>
                  <Input
                    data-testid="signup-otp-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="••••••"
                    className="mt-1.5 text-center font-mono text-2xl tracking-[0.5em]"
                  />
                  <div className="mt-2 flex items-center justify-between text-[10px] text-ink-muted">
                    <span className="inline-flex items-center gap-1"><Mail size={11} /> Code expires in 15 min</span>
                    <button
                      type="button"
                      onClick={resendOtp}
                      disabled={resendCooldown > 0 || resendBusy}
                      className="inline-flex items-center gap-1 font-medium text-brand hover:underline disabled:cursor-not-allowed disabled:text-ink-muted disabled:no-underline"
                      data-testid="signup-resend-otp"
                    >
                      {resendBusy ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                    </button>
                  </div>
                </div>

                {error && (
                  <div data-testid="signup-error" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{error}</div>
                )}

                <Button
                  data-testid="signup-submit-step2"
                  type="submit"
                  disabled={submitting || otp.length < 4}
                  className="w-full bg-brand hover:bg-brand-700"
                >
                  {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Verifying…</> : <>Verify & launch workspace <ArrowRight size={14} className="ml-2" /></>}
                </Button>
              </form>
            </>
          )}

          {/* ---------- Step 3: success ---------- */}
          {step === 3 && completed && (
            <div className="space-y-5" data-testid="signup-success">
              <div className="grid h-14 w-14 place-items-center rounded-full bg-leaf/15 text-leaf-700">
                <CheckCircle2 size={28} />
              </div>
              <div>
                <h2 className="font-heading text-3xl font-semibold">You're in! 🎉</h2>
                <p className="mt-1 text-sm text-ink-muted">
                  Workspace <b className="text-ink">{completed.hospital.name}</b> is ready.
                </p>
              </div>

              <Card className="space-y-2.5 p-4 text-sm">
                <div className="flex justify-between"><span className="text-ink-muted">Hospital code</span><code className="rounded bg-secondary px-2 py-0.5 font-mono">{completed.hospital.code}</code></div>
                <div className="flex justify-between"><span className="text-ink-muted">Plan</span><b>{completed.hospital.plan?.name}</b></div>
                <div className="flex justify-between"><span className="text-ink-muted">Trial valid until</span><b>{completed.hospital.valid_until}</b></div>
                <div className="flex justify-between"><span className="text-ink-muted">Sign-in email</span><b>{completed.user.email}</b></div>
              </Card>

              <Button
                data-testid="signup-go-to-app"
                onClick={goToApp}
                className="w-full bg-brand hover:bg-brand-700"
              >
                Open my dashboard <ArrowRight size={14} className="ml-2" />
              </Button>
              <p className="text-center text-[11px] text-ink-muted">
                Tip: invite your team from <i>Admin → Users</i> after you sign in.
              </p>
            </div>
          )}

          <div className="mt-8 text-center text-[11px] text-ink-muted">
            <Link to="/" className="hover:text-brand">← Back to home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
